<template>
    <el-dialog
        :title="user.phantom ? $t('Add New User') : $t('Edit User')"
        :visible.sync="show"
        append-to-body
        custom-class="usermodal"
        v-loading="loading || authorityLoading"
        :close-on-click-modal="false"
        :width="!$client.isMobile ? '50%' : '90%'"
    >
        <el-form
            :label-width="!$client.isMobile ? '200px' : '100px'"
            label-position="left"
            :model="user"
            :rules="rules"
            :validate-on-rule-change="false"
            ref="form"
        >
            <el-form-item :label="$t('Organization')" v-if="organization && organization.name">
                <div>{{ organization.name }}</div>
            </el-form-item>
            <el-form-item :label="$t('First Name')" prop="first_name">
                <el-input :placeholder="$t('First Name')" v-model="user.first_name" size="small" />
            </el-form-item>
            <el-form-item :label="$t('Last Name')" prop="last_name">
                <el-input :placeholder="$t('Last Name')" v-model="user.last_name" size="small" />
            </el-form-item>
            <el-form-item :label="$t('Email')" prop="email">
                <el-input
                    placeholder="user@virtualriskspace.com"
                    v-model="user.email"
                    size="small"
                    :disabled="emailEnabled ? false : !isContextUserRootAdmin"
                    :readOnly="emailEnabled ? false : !isContextUserRootAdmin"
                />
            </el-form-item>
            <el-form-item :label="$t('Phone')" v-if="hasFullUserInfo">
                <vue-tel-input
                    mode="international"
                    :required="true"
                    :validCharactersOnly="true"
                    :placeholder="$t('Enter a phone number')"
                    v-model="user.cell_phone"
                    v-bind="bindProps"
                ></vue-tel-input>
            </el-form-item>
            <el-form-item :label="$t('Time Zone')" prop="time_zone" v-if="hasFullUserInfo">
                <el-select v-model="user.time_zone" filterable size="small" class="w-100">
                    <el-option
                        v-for="zone in moment.tz.getZones()"
                        :key="zone.value"
                        :label="zone.label"
                        :value="zone.value"
                    ></el-option>
                </el-select>
            </el-form-item>
            <template v-if="user.phantom || user.password_available">
                <el-form-item
                    :label="user.phantom ? $t('Password') : $t('New Password')"
                    prop="password"
                >
                    <passwordfield
                        ref="passwordField"
                        v-model="user.password"
                        size="small"
                        placement="bottom"
                        placeholder="******"
                    />
                </el-form-item>
                <el-form-item
                    :label="user.phantom ? $t('Confirm Password') : $t('Confirm New Password')"
                    prop="confirm_password"
                >
                    <el-input
                        v-model="user.confirm_password"
                        placeholder="******"
                        type="password"
                        autocomplete="off"
                        size="small"
                        show-password
                    />
                </el-form-item>
                <el-form-item
                    v-if="isEditingSelf && isCredentialsChanged"
                    :label="$t('Current Password')"
                    prop="current_password"
                >
                    <el-input
                        v-model="user.current_password"
                        placeholder="******"
                        type="password"
                        autocomplete="off"
                        size="small"
                    />
                </el-form-item>
                <el-form-item
                    :label="$t('Role')"
                    prop="access_role_id"
                    :class="!roleFieldEnabled ? 'display-none' : ''"
                >
                    <el-select
                        v-model="user.access_role_id"
                        class="pull-left"
                        size="small"
                        style="width: 300px"
                    >
                        <el-option
                            v-for="item in availableRoleList"
                            :key="item.id"
                            :label="item.roleName"
                            :value="item.id"
                        ></el-option>
                    </el-select>
                </el-form-item>
                <el-form-item :label="$t('Country Authority')" v-if="insuredUserMode">
                    <div>
                        <el-radio v-model="user.country_authority_level" label="all">{{
                            $t('All Countries')
                        }}</el-radio>
                        <el-radio v-model="user.country_authority_level" label="specific">{{
                            $t('Specific Countries')
                        }}</el-radio>
                        <el-radio v-model="user.country_authority_level" label="except">{{
                            $t('All Countries Except')
                        }}</el-radio>
                    </div>
                    <el-select
                        v-if="user.country_authority_level != 'all'"
                        v-model="user.authority_country_codes"
                        value-key="id"
                        size="small"
                        :filterable="true"
                        multiple
                        clearable
                        style="width: 100%"
                    >
                        <el-option
                            v-for="item in countryList"
                            :key="item.name"
                            :label="item.name"
                            :value="item.code"
                        >
                        </el-option>
                    </el-select>
                </el-form-item>
            </template>
        </el-form>
        <div slot="footer" class="dialog-footer">
            <el-button
                v-if="hasFullUserInfo && isAdmin && isEditingSelf && !deleteAccountRequestSent"
                size="small"
                type="danger"
                :loading="loading"
                @click="onDeleteAccount"
                >{{ $t('Delete Account') }}</el-button
            >
            <el-button size="small" type="primary" :loading="loading" @click="onSave(false)">
                <i class="fa fa-save"></i>
                {{ $t('Save') }}
            </el-button>
            <el-button size="small" type="text" @click="onCancel">{{ $t('Close') }}</el-button>
        </div>
    </el-dialog>
</template>

<script>
import moment from 'moment-timezone';
import * as firebase from 'firebase/app';
import { DEFAULT_ERROR_MESSAGE } from '@app/common/utils/constants';
import passwordfield from '@/components/passwordfield.vue';
import Model from '@/lib/data/Model';
import Collection from '@/lib/data/Collection';

import countryList from '../../../static/country-list.json';

export default {
    name: 'usermodal',
    components: { passwordfield },
    props: {
        value: Boolean,
        user: null,
        organization: null,
        hasFullUserInfo: {
            type: Boolean,
            default: true,
        },
        defaultAccessRole: {
            type: String,
            default() {
                return 'admin';
            },
        },
        emailEnabled: {
            type: Boolean,
            default: false,
        },
        roleEnabled: {
            type: Boolean,
            default: false,
        },
        insuredUserMode: {
            type: Boolean,
            default: false,
        },
        authorityLoading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            deleteAccountRequestSent: false,
            initialEmail: this.user.email,
            moment,
            bindProps: {
                mode: 'international',
            },
            countryList,
        };
    },
    mounted() {
        if (this.isEditingSelf) {
            // trigger firebase user to be found in context
            this.$firebase.auth().onAuthStateChanged((user) => {
                if (!user) {
                    window.location.href = '/authorize';
                }
            });
        }

        if (!this.accessRoleList.loaded) {
            this.accessRoleList.load(true);
        } else {
            this.user.access_role_id = this.user.access_role_id || this.defaultAccessRoleId;
        }
    },
    computed: {
        show: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            },
        },
        context() {
            return this.$store.getters.context;
        },
        isAdmin() {
            return this.context.isAdmin;
        },
        isEditingSelf() {
            return this.context.user === this.user;
        },
        isCredentialsChanged() {
            return this.user.password || this.initialEmail !== this.user.email;
        },
        isContextUserRootAdmin() {
            return this.$store.getters.isContextUserRootAdmin;
        },
        accessRoleList() {
            return this.$store.getters.accessRoleList;
        },
        availableRoleList() {
            return this.insuredUserMode
                ? this.accessRoleList.filter(
                      (item) => ['insured', 'super_insured'].indexOf(item.name) > -1
                  )
                : this.accessRoleList.filter(
                      (item) =>
                          [
                              'admin',
                              'agent',
                              'insurer_admin',
                              'fnol_admin',
                              'policy_viewer',
                          ].indexOf(item.name) > -1
                  );
        },
        rules() {
            return {
                first_name: [
                    {
                        required: true,
                        message: this.$t('Please enter first name'),
                        trigger: 'blur',
                    },
                ],
                last_name: [
                    {
                        required: true,
                        message: this.$t('Please enter last name'),
                        trigger: 'blur',
                    },
                ],
                email: [
                    {
                        required: true,
                        message: this.$t('Please enter email address'),
                        trigger: 'blur',
                    },
                ],
                time_zone: [
                    {
                        required: this.hasFullUserInfo,
                        message: this.$t('Please select your time zone'),
                        trigger: 'blur',
                    },
                ],
                password: [
                    {
                        required: !this.user?.id && this.user.password_available,
                        trigger: 'blur',
                        validator: (rule, value, callback) => {
                            let error;

                            if (!this.user?.id && this.user.password_available) {
                                if (!value) {
                                    error = new Error(this.$t('Please enter password'));
                                } else if (!this.$refs.passwordField.isValid()) {
                                    error = new Error(this.$t('Please enter a valid password'));
                                }
                            }

                            callback(error);
                        },
                    },
                ],
                confirm_password: [
                    {
                        required: !!this.user.password,
                        trigger: 'blur',
                        validator: (rule, value, callback) => {
                            if (this.user.password) {
                                if (!value) {
                                    callback(new Error(this.$t('Please enter confirm password')));
                                    return;
                                }

                                if (this.user.password !== this.user.confirm_password) {
                                    callback(new Error(this.$t('Passwords do not match')));
                                    return;
                                }
                            }

                            callback();
                        },
                    },
                ],
                current_password: [
                    {
                        required:
                            this.hasFullUserInfo &&
                            this.isEditingSelf &&
                            this.user.password_available &&
                            this.isCredentialsChanged,
                        message: this.$t('Please enter your current password'),
                        trigger: 'blur',
                    },
                ],
                access_role_id: [
                    {
                        required: true,
                    },
                ],
            };
        },
        defaultAccessRoleId() {
            const defaultAccessRole = this.accessRoleList.find(
                (item) => item.name == this.defaultAccessRole
            );
            return defaultAccessRole ? defaultAccessRole.id : null;
        },
        roleFieldEnabled() {
            return this.roleEnabled;
        },
    },
    watch: {
        defaultAccessRoleId(value) {
            this.user.access_role_id = this.user.access_role_id || value;
        },
    },
    methods: {
        reauthenticateWithCredential(email, password) {
            return new Promise((resolve) => {
                const cred = firebase.auth.EmailAuthProvider.credential(email, password);

                this.$firebase
                    .auth()
                    .currentUser.reauthenticateWithCredential(cred)
                    .then(() => resolve([]))
                    .catch((error) => {
                        this.$notify.error({
                            title: this.$t('Error'),
                            message: error.message,
                        });

                        resolve([error]);
                    });
            });
        },
        async onSave() {
            this.$refs.form.validate(async (valid) => {
                if (valid) {
                    if (this.isEditingSelf && this.isCredentialsChanged) {
                        const [error] = await this.reauthenticateWithCredential(
                            this.initialEmail,
                            this.user.current_password
                        );

                        if (error) {
                            return;
                        }
                    }

                    this.user.insured_user_mode = this.insuredUserMode;

                    this.user.save({
                        beforesend: () => {
                            this.loading = true;
                        },
                        complete: () => {
                            this.loading = false;
                        },
                        success: async (op, response) => {
                            const { newToken } = response.data;

                            if (
                                this.isEditingSelf &&
                                this.isCredentialsChanged &&
                                !this.user.password_available
                            ) {
                                this.user.password_available = true;
                            }

                            // update the existing token if new one is received
                            if (this.isEditingSelf && newToken) {
                                Model.setAuthToken(newToken);
                                Collection.setAuthToken(newToken);
                                window.localStorage.setItem('auth_token', newToken);
                            }

                            // We have to re-authenticate after user credentials changed.
                            if (this.isEditingSelf && this.isCredentialsChanged) {
                                const [error] = await this.reauthenticateWithCredential(
                                    this.user.email,
                                    // if user changed his password we should use the new one, otherwise use the current one.
                                    this.user.password || this.user.current_password
                                );

                                if (error) {
                                    return;
                                }
                            }

                            this.user.password = null;
                            this.user.confirm_password = null;
                            this.user.current_password = null;

                            if (
                                this.isEditingSelf &&
                                moment.defaultZone.name !== this.user.time_zone
                            ) {
                                this.$notify.success({
                                    title: this.$t('Success'),
                                    message: `${this.$t('{0} has been saved successfully.', {
                                        0: this.user.fullName,
                                    })} ${this.$t('Reloading due to time zone change.')}`,
                                });

                                setTimeout(() => {
                                    window.location.reload();
                                }, 3000);
                            } else {
                                this.$notify.success({
                                    title: this.$t('Success'),
                                    message: this.$t('{0} has been saved successfully.', {
                                        0: this.user.fullName,
                                    }),
                                });

                                this.$store.dispatch('fetchContext');
                                this.show = false;
                            }
                        },
                        failure: (op, response) => {
                            this.$notify.error({
                                title: this.$t('Error'),
                                message:
                                    response && response.meta
                                        ? response.meta.display_message
                                        : this.$t(DEFAULT_ERROR_MESSAGE),
                            });
                        },
                    });
                }
            });
        },
        onCancel() {
            this.user.reject();
            this.show = false;
        },
        onDeleteAccount() {
            this.$confirm(
                this.$t(
                    'Are you sure you want to delete your account? If you confirm, a confirmation email will be sent to proceed.'
                ),
                this.$t('Warning'),
                {
                    confirmButtonText: this.$t('Yes'),
                    cancelButtonText: this.$t('Cancel'),
                    type: 'warning',
                }
            ).then(() => {
                this.context.organization.deleteAccountRequest({
                    beforesend: () => {
                        this.loading = true;
                    },
                    complete: () => {
                        this.loading = false;
                    },
                    success: () => {
                        this.deleteAccountRequestSent = true;
                        this.$notify.success({
                            title: this.$t('Success'),
                            message: this.$t(
                                'The confirmation email has been sent. Please check your inbox.'
                            ),
                        });
                    },
                    failure: (op, response) => this.handleFailure(op, response),
                });
            });
        },
    },
};
</script>
<style lang="scss" scoped>
@import '@/styles/responsive.scss';

.usermodal {
    margin: 0 0.1rem !important;
}
</style>
